import * as React from "react";
import * as Yup from "yup";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import "../../../../../theme/style.scss";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import { handleFocus } from "../../../../../shared/CommonFunction";
import {
  createInputPropsForNumberField,
  createInputPropsWithSeparator,
} from "../../../../../shared/data";
import CustomTooltip from "../../../../../components/ToolTip/CustomToolTip";
import SkeletonWrapper from "../../../../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../../../../components/CustomFields/TextField";
import {
  BPMesurementDefaultOptions,
  SpO2MesurementDefaultOptions,
  temperatureByPalpationOptions,
  temperatureOptions,
} from "../../../../../shared/Data/FrontDesk/Appointment";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../../../../components/CustomFields/CustomSelect";
import { OPD } from "../../../../../services/models/OPD/OPD";
import { UpdateAppointmentVitalForOPD } from "../../../../../services/AppointmentService";
import { AlertType } from "../../../../../services/models/AlertType";
import { PreferencesContext } from "../../../../../PreferenceContext";
import { GetItemList } from "../../../../../services/ItemService";
import { PAGE_SIZE } from "../../../../../services/common/const";
import { mode } from "crypto-ts";
// import { Props } from "react-select";

export type Props = {
  handleClosePopUp: any;
  opd: OPD;
};

interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR FORM

interface formInitialValueTypes {
  opdId: number | null;
  appointmentId: number | null;
  consumerId: number | null;
  orgId: number | null;
  branchId: number | null;
  departmentId: number | null;
  specialityId: number | null;
  patientId: number | null;

  weight: number | null;
  height: number | null;
  //Pulse
  pr: number;

  //Respiratory Rate
  respiratoryRate: number;

  //Spo2
  spO2SiteUnit: string;
  reasonForVisitColl: any[];
  spO2Amount: number | null;

  //Blood Pressure
  bpLeftAmount: number;
  bpRightAmount: number;
  bpDisplayValue: string;
  bpSiteUnit: string;

  //Temperatue  ====
  temperatureAmount: number;
  temperatureUnit: string | null;
  temperatureByPalpation: selectOption | null;

  //RBS
  rbs: number;

  // Head Circumference
  headCircumference: number | null;

  // MOTHER'S HEIGHT
  motherHeight: number | null;

  // FATHER'S HEIGHT
  fatherHeight: number | null;
  memberPhysicalGrowthId: number | null;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

const VitalAndAnthropometry: React.FC<Props> = React.memo(
  ({ handleClosePopUp, opd }) => {
    const { userContext, updateUserContext } =
      React.useContext(PreferencesContext);
    // DEFAULT SCHEMA VALUE FOR FORM
    const initAppointmentData: formInitialValueTypes = {
      opdId: opd?.opdId || 0,
      appointmentId: opd?.appointmentId || 0,
      consumerId: userContext?.consumerId || 0,
      orgId: userContext?.organizationId || 0,
      branchId: userContext?.branchId || 0,
      departmentId: opd?.departmentId || 0,
      specialityId: opd?.specialityId || 0,
      patientId: opd?.patientId || 0,

      weight: opd?.weight || 0,
      height: opd?.height || 0,
      //Pulse
      pr: opd?.pulseRate || 0,
      //Respiratory
      respiratoryRate: opd?.respiratoryRate,
      //Spo2
      spO2SiteUnit: "RIGHT_UPPER_LIMB",
      spO2Amount: opd?.spo2Amount || 0,
      //Blood Pressure
      bpLeftAmount: opd?.bpLeftAmount || 0,
      bpRightAmount: opd?.bpRightAmount || 0,
      bpDisplayValue: opd?.bpDisplayValue || "",
      bpSiteUnit: "RIGHT_UPPER_LIMB",
      //Temperature
      temperatureUnit: opd?.temperatureUnit || "",
      temperatureAmount: opd?.temperatureAmount || 0,
      temperatureByPalpation: opd?.temperatureByPalpation
        ? {
          label: opd?.temperatureByPalpation,
          value: opd?.temperatureByPalpation,
        }
        : null,

      // Rbs
      rbs: opd?.rbs || 0,

      // HC
      headCircumference: opd?.headCircumference || 0,

      // Mother's Height
      motherHeight: opd?.motherHeight || 0,

      // Father's Height
      fatherHeight: opd?.fatherHeight || 0,
      memberPhysicalGrowthId: opd?.memberPhysicalGrowthId || 0,
      reasonForVisitColl: (() => {
        // Handle array of objects from OPD Clinical Detail
        if (
          Array.isArray(opd?.reasonForVisitColl) &&
          opd.reasonForVisitColl.length > 0
        ) {
          return opd.reasonForVisitColl.map((item: any) => ({
            label: item.itemName || item.label,
            value: item.itemId?.toString() || item.value,
            appointmentReasonForVisitId:
              item.appointmentReasonForVisitId?.toString() || 0,
            mode: item?.mode || "U",
          }));
        }
        // Handle string from reasonsForVisit field
        if (!opd?.reasonsForVisit) return [];
        try {
          return JSON.parse(opd.reasonsForVisit);
        } catch {
          return opd.reasonsForVisit.split(",").map((item: string) => ({
            label: item.trim(),
            value: item.trim(),
          }));
        }
      })(),
    };

    const theme = useTheme();
    // BREAK POINTS
    const smd = useMediaQuery(theme.breakpoints.down("sm"));

    //INPUT PROPS
    const weightProps = createInputPropsForNumberField(3, 3, true);
    const heightProps = createInputPropsForNumberField(3, 1, true);
    const headCircumferenceProps = createInputPropsForNumberField(3, 1, true);
    const prProps = createInputPropsForNumberField(3, 0, false);
    const rrProps = createInputPropsForNumberField(3, 0, false);
    const spo2Props = createInputPropsForNumberField(3, 0, false);
    const tempetureProps = createInputPropsForNumberField(3, 1, true);
    const bpProps = createInputPropsWithSeparator(3, 3, "/", 300);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [itemColl, setItemColl] = useState<any[]>([]);
    const [param, setParam] = useState(parameters);
    const isSuperAdmin =
      (userContext?.loginDetail?.userRole || "").toString().toLowerCase() ===
      "superadmin";

    //SCHEMA VALIDATION
    const validationSchema = Yup.object().shape({
      reasonForVisitColl: Yup.array()
        .min(1, "Please select at least one reason for visit")
        .required("Reason for visit is required"),
      weight: Yup.number()
        .max(999, "Max weight limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      height: Yup.number()
        .max(999, "Max height limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      pr: Yup.number()
        .max(999, "Max pulse rate limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      respiratoryRate: Yup.number()
        .max(999, "Max respiratory rate limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      spO2Amount: Yup.number()
        .max(100, "Max amount required is 100")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      spO2SiteUnit: Yup.string().nullable(),
      bpLeftAmount: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      bpRightAmount: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      bpDisplayValue: Yup.string().nullable(),
      bpSiteUnit: Yup.string().nullable(),
      temperatureAmount: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
      temperatureUnit: Yup.string().nullable(),

      temperatureByPalpation: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),

      rbs: Yup.number()
        .max(999, "Max Rbs limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),

      headCircumference: Yup.number()
        .max(999, "Max headCircumference limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),

      motherHeight: Yup.number()
        .max(999, "Max MotherHeight limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),

      fatherHeight: Yup.number()
        .max(999, "Max FatherHeight limit is 999")
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        ),
    });

    // FORM HOOK
    const methods = useForm({
      resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
      defaultValues: initAppointmentData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
    });

    // SAVE AND UPDATE Vitals
    const updateVitalsAndAnthropometry = async (
      value: formInitialValueTypes
    ) => {
      try {
        let result: any = "";
        const reasonForVisitColl =
          value?.reasonForVisitColl?.map((item: any, index: number) => {
            return {
              displayOrder: index,
              itemId: item?.value,
              itemName: item?.label,
              isDeleted: item?.isDeleted || false,
              appointmentReasonForVisitId:
                item?.appointmentReasonForVisitId || null,
              mode: item?.mode || "I",
            };
          }) || [];
        let formData = {
          ...initAppointmentData,
          weight: value.weight || 0,
          height: value.height || 0,
          pr: value.pr || 0,
          respiratoryRate: value?.respiratoryRate || 0,
          spO2SiteUnit: value?.spO2SiteUnit || "",
          spO2Amount: value?.spO2Amount || 0,
          bpLeftAmount: value?.bpLeftAmount || 0,
          bpRightAmount: value?.bpRightAmount || 0,
          bpSiteUnit: value?.bpSiteUnit || "",
          bpDisplayValue: value?.bpDisplayValue || "",
          temperatureUnit: value?.temperatureUnit || "",
          temperatureAmount: value?.temperatureAmount || 0,
          temperatureByPalpation: value?.temperatureByPalpation?.value || "",
          rbs: value.rbs || 0,
          headCircumference: value.headCircumference || 0,
          motherHeight: value.motherHeight || 0,
          fatherHeight: value.fatherHeight || 0,
          memberPhysicalGrowthId: opd?.memberPhysicalGrowthId || 0,
          reasonForVisitJson: JSON.stringify(reasonForVisitColl) || [],
        };
        if (value) {
          result = await UpdateAppointmentVitalForOPD(formData);
        }
        if (result?.data) {
          handleClosePopUp("CREATE", value);
        }
      } catch (e) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage:
            typeof e === "string" ? (e as string) : (e?.message as string),
          alertType: AlertType.Error,
        });
      }
    };

    // ON SUBMIT OF FORM
    const onSubmit = async (data: any) => {
      setIsLoading(true);
      await updateVitalsAndAnthropometry(data);
      setIsLoading(false);
    };

    // GET ITEMS
    async function fetchItem(searchValue: string, oninit: boolean) {
      try {
        let itemCollection = [];
        let params = new URLSearchParams();
        params.append("consumerId", userContext.consumerId?.toString());
        params.append("orgId", userContext.organizationId?.toString());
        params.append("branchId", userContext.branchId?.toString());
        params.append(
          "departmentId",
          isSuperAdmin ? "0" : userContext?.departmentId?.toString() || "0",
        );
        params.append(
          "specialityId",
          isSuperAdmin ? "0" : userContext?.specialityId?.toString() || "0",
        );
        params.append("pageIndex", param.pageIndex?.toString());
        params.append("pageSize", param.pageSize?.toString());
        params.append("sortColumn", param.sortColumn);
        params.append("sortDirection", param.sortDirection);
        params.append("searchValue", searchValue);
        params.append("mode", "DROPDOWN".toString());
        params.append("productTypeCodes", 'HSN,SAC');
        const itemData: any = await GetItemList(params);
        if (itemData?.data?.data?.itemColl) {
          itemCollection = itemData.data.data.itemColl;
          if (oninit) setItemColl(itemCollection);
        }
        return itemCollection;
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage:
            typeof e === "string" ? (e as string) : (e?.message as string),
          alertType: AlertType.Error,
        });
      }
    }

    // FILTER ITEM
    async function filterItem(inputValue: string) {
      let result = await fetchItem(inputValue, false);
      return result;
    }

    // ITEM LOAD ON SEARCH
    const loadItemList = (
      inputValue: string,
      callback: (options: any) => void
    ) => {
      if (inputValue && inputValue.length > 0) {
        filterItem(inputValue?.trim()).then((r) => {
          callback(r);
        });
      } else {
        callback([]);
      }
    };

    // FETCH DATA
    const fetchData = async () => {
      try {
        await fetchItem("", true);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        console.log(e);
      }
    };

    // USEEFFECTS
    useEffect(() => {
      setIsLoading(true);
      fetchData();

      // Handle array of objects from OPD Clinical Detail
      if (
        Array.isArray(opd?.reasonForVisitColl) &&
        opd.reasonForVisitColl.length > 0
      ) {
        const reasons = opd.reasonForVisitColl.map((item: any) => ({
          label: item.itemName || item.label,
          value: item.itemId?.toString() || item.value,
          appointmentReasonForVisitId:
            item.appointmentReasonForVisitId?.toString() || 0,
          mode: item?.mode || "U",
        }));
        methods.setValue("reasonForVisitColl", reasons);
      }
      // Handle string from reasonsForVisit field
      else if (opd?.reasonsForVisit) {
        try {
          const parsedReasons = JSON.parse(opd.reasonsForVisit);
          methods.setValue("reasonForVisitColl", parsedReasons);
        } catch (e) {
          const reasons = opd.reasonsForVisit
            .split(",")
            .map((item: string) => ({
              label: item.trim(),
              value: item.trim(),
            }));
          methods.setValue("reasonForVisitColl", reasons);
        }
      }
    }, [opd]);

    return (
      <React.Fragment>
        <Box className={"main-container"}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.reasonForVisitColl
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Reason for Visit *
                      </FormLabel>
                      <SkeletonWrapper className="skeleton-input">
                        <Controller
                          control={methods.control}
                          name={`reasonForVisitColl`}
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              id={`reasonForVisitColl`}
                              FieldName={`reasonForVisitColl`}
                              isMulti
                              placeholder="Search or create Reason for Visit"
                              defaultOptions={itemColl}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              hideSelectedOptions
                              onSelectResetsInput={false}
                              closeMenuOnSelect={false}
                              loadOptions={loadItemList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e: any) => {
                                const visitColl =
                                  e?.map((item: any, index: number) => {
                                    return {
                                      ...item,
                                      displayOrder: index,
                                    };
                                  }) || [];
                                methods.setValue(
                                  "reasonForVisitColl",
                                  visitColl,
                                  {
                                    shouldValidate: true,
                                  }
                                );
                              }}
                            // onCreateOption={(inputvalue) => {
                            //   handleClickOpen(true, inputvalue, "ITEM");
                            // }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.reasonForVisitColl?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      Vitals and Anthropometry
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.weight
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Weight{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="000.000"
                          fullWidth
                          id="weight"
                          size="small"
                          inputProps={{
                            ...weightProps,
                          }}
                          {...methods.register("weight")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">Kg</InputAdornment>
                            ),
                          }}
                          error={!!methods.formState.errors.weight}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.weight?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.height
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Height/Length{" "}
                        <CustomTooltip
                          title="This field is important for automated dose calculation"
                          hideHeader={true}
                        ></CustomTooltip>
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="000.0"
                          fullWidth
                          id="height"
                          size="small"
                          inputProps={{
                            ...heightProps,
                          }}
                          {...methods.register("height")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">cm</InputAdornment>
                            ),
                          }}
                          error={!!methods.formState.errors.height}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.height?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  {/* HEAD CIRCUMFERENCE */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.headCircumference
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        HC
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="000.0"
                          fullWidth
                          id="headCircumference"
                          size="small"
                          inputProps={{
                            ...headCircumferenceProps,
                          }}
                          {...methods.register("headCircumference")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">cm</InputAdornment>
                            ),
                          }}
                          error={!!methods.formState.errors.headCircumference}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.headCircumference?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.pr
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Pulse Rate
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0"
                          fullWidth
                          id="pr"
                          size="small"
                          inputProps={{
                            ...prProps,
                          }}
                          {...methods.register("pr")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                bpm
                              </InputAdornment>
                            ),
                          }}
                          error={!!methods.formState.errors.pr}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.pr?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.respiratoryRate
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Respiratory Rate
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0"
                          fullWidth
                          id="respiratoryRate"
                          size="small"
                          inputProps={{
                            ...rrProps,
                          }}
                          {...methods.register("respiratoryRate")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                /min
                              </InputAdornment>
                            ),
                          }}
                          error={!!methods.formState.errors.respiratoryRate}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.respiratoryRate?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.spO2Amount
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        {smd ? "SPO2" : "SPO2 with Site of Measurement"}
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0"
                          fullWidth
                          id="spO2Amount"
                          size="small"
                          inputProps={{
                            ...spo2Props,
                          }}
                          onFocus={handleFocus}
                          {...methods.register("spO2Amount", {
                            onChange(event) {
                              methods.setValue(
                                "spO2Amount",
                                event.target.value,
                                {
                                  shouldValidate: true,
                                }
                              );
                            },
                          })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography sx={{ mr: 1 }}>%</Typography>
                                  <Box
                                    className="input-end-andorment-border"
                                    display={{ xs: "none", sm: "block" }}
                                  >
                                    <Select
                                      value={methods.getValues().spO2SiteUnit}
                                      onChange={(e) => {
                                        methods.setValue(
                                          "spO2SiteUnit",
                                          e.target.value,
                                          { shouldValidate: true }
                                        );
                                      }}
                                      size="small"
                                      displayEmpty
                                      inputProps={{
                                        "aria-label": "Without label",
                                      }}
                                      sx={{
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none",
                                        },
                                        ".MuiSelect-select": {
                                          paddingRight: "0px",
                                        },
                                      }}
                                    >
                                      {SpO2MesurementDefaultOptions?.map(
                                        (x: any, index: number) => (
                                          <MenuItem
                                            key={index?.toString()}
                                            value={x.value}
                                          >
                                            {x.label}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={!!methods.formState.errors.spO2Amount}
                        />
                      </SkeletonWrapper>
                    </FormControl>

                    <Typography className="validation-message">
                      {methods.formState.errors.spO2Amount?.message}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    display={{
                      sm: "none",
                    }}
                  >
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text ${methods.formState.errors.spO2SiteUnit
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Site of SPO2 Measurement *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Select
                          className="small-scrren-uints"
                          value={methods.getValues().spO2SiteUnit}
                          onChange={(e) => {
                            methods.setValue("spO2SiteUnit", e.target.value, {
                              shouldValidate: true,
                            });
                          }}
                          size="small"
                          displayEmpty
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            ".MuiSelect-select": {
                              paddingRight: "0px",
                            },
                          }}
                          error={!!methods.formState.errors.spO2SiteUnit}
                        >
                          {SpO2MesurementDefaultOptions?.map((x, index) => (
                            <MenuItem key={index.toString()} value={x.value}>
                              {x.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.bpLeftAmount
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        {smd ? "BP" : "BP with site of Measurement"}
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="000/000"
                          fullWidth
                          id="bpLeftAmount"
                          size="small"
                          inputProps={bpProps}
                          onFocus={handleFocus}
                          {...methods.register("bpDisplayValue", {
                            onChange(event) {
                              let inputvalue = event.target.value;
                              const [before, after] = inputvalue?.split("/");
                              methods.setValue(
                                "bpLeftAmount",
                                before ? parseInt(before, 10) : 0,
                                {
                                  shouldValidate: true,
                                }
                              );
                              methods.setValue(
                                "bpRightAmount",
                                after ? parseInt(after, 10) : 0,
                                {
                                  shouldValidate: true,
                                }
                              );
                              methods.setValue(
                                "bpDisplayValue",
                                inputvalue || null,
                                {
                                  shouldValidate: true,
                                }
                              );
                            },
                          })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography sx={{ mr: 1 }}>mmHg</Typography>
                                  <Box
                                    className="input-end-andorment-border"
                                    display={{ xs: "none", sm: "block" }}
                                  >
                                    <Select
                                      value={methods.getValues().bpSiteUnit}
                                      onChange={(e) => {
                                        methods.setValue(
                                          "bpSiteUnit",
                                          e.target.value,
                                          { shouldValidate: true }
                                        );
                                      }}
                                      size="small"
                                      displayEmpty
                                      inputProps={{
                                        "aria-label": "Without label",
                                      }}
                                      sx={{
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none",
                                        },
                                        ".MuiSelect-select": {
                                          paddingRight: "0px",
                                        },
                                      }}
                                    >
                                      {BPMesurementDefaultOptions?.map(
                                        (x: any, index: number) => (
                                          <MenuItem
                                            key={index?.toString()}
                                            value={x.value}
                                          >
                                            {x.label}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={!!methods.formState.errors.bpLeftAmount}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.bpLeftAmount?.message}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    display={{
                      sm: "none",
                    }}
                  >
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text ${methods.formState.errors.bpSiteUnit
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        site of BP Measurement *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Select
                          className="small-scrren-uints"
                          value={methods.getValues().bpSiteUnit}
                          onChange={(e) => {
                            methods.setValue("bpSiteUnit", e.target.value, {
                              shouldValidate: true,
                            });
                          }}
                          size="small"
                          displayEmpty
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          sx={{
                            ".MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            ".MuiSelect-select": {
                              paddingRight: "0px",
                            },
                          }}
                        >
                          {BPMesurementDefaultOptions?.map(
                            (x: any, index: number) => (
                              <MenuItem key={index?.toString()} value={x.value}>
                                {x.label}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.temperatureAmount
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Temperature
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="000.0"
                          fullWidth
                          id="temperatureAmount"
                          size="small"
                          inputProps={tempetureProps}
                          onFocus={handleFocus}
                          {...methods.register("temperatureAmount", {
                            onChange(event) {
                              methods.setValue(
                                "temperatureAmount",
                                event.target.value,
                                {
                                  shouldValidate: true,
                                }
                              );
                            },
                          })}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box className="input-end-andorment-border">
                                    <Select
                                      value={
                                        methods.getValues().temperatureUnit
                                      }
                                      onChange={(e) => {
                                        methods.setValue(
                                          "temperatureUnit",
                                          e.target.value,
                                          { shouldValidate: true }
                                        );
                                      }}
                                      size="small"
                                      displayEmpty
                                      inputProps={{
                                        "aria-label": "Without label",
                                      }}
                                      sx={{
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none",
                                        },
                                        ".MuiSelect-select": {
                                          paddingRight: "0px",
                                        },
                                      }}
                                    >
                                      {temperatureOptions?.map(
                                        (x: any, index: number) => (
                                          <MenuItem
                                            key={index?.toString()}
                                            value={x.value}
                                          >
                                            {x.label}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={!!methods.formState.errors.temperatureAmount}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.temperatureAmount?.message}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.temperatureByPalpation
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Temperature by Palpation
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="temperatureByPalpation"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"temperatureByPalpation"}
                              FieldName={"temperatureByPalpation"}
                              options={temperatureByPalpationOptions}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "temperatureByPalpation",
                                  e !== null ? e : null,
                                  { shouldValidate: true }
                                );
                              }}
                              className={`text-input ${methods.formState.errors.temperatureByPalpation
                                ? "error"
                                : ""
                                }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {
                          methods.formState.errors.temperatureByPalpation
                            ?.message
                        }
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.rbs
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        RBS
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0"
                          fullWidth
                          id="rbs"
                          size="small"
                          inputProps={{
                            ...prProps,
                          }}
                          {...methods.register("rbs")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                mg/dl
                              </InputAdornment>
                            ),
                          }}
                          error={!!methods.formState.errors.rbs}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.rbs?.message}
                      </Typography>
                    </FormControl>
                  </Grid>

                  {/* <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className={`fileds-label-text  ${methods.formState.errors.motherHeight
                      ? "validation-message"
                      : ""
                      }`}
                  >
                    Mother's Height{" "}
                    <CustomTooltip
                      title="This field is important for automated dose calculation"
                      hideHeader={true}
                    ></CustomTooltip>
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input"
                  >
                    <CustomTextField
                      placeholder="000.0"
                      fullWidth
                      id="motherHeight"
                      size="small"
                      inputProps={{
                        ...heightProps,
                      }}
                      {...methods.register("motherHeight")}
                      onFocus={handleFocus}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">cm</InputAdornment>
                        ),
                      }}
                      error={!!methods.formState.errors.motherHeight}
                    />
                  </SkeletonWrapper>
                  <Typography className="validation-message">
                    {methods.formState.errors.motherHeight?.message}
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel
                    className={`fileds-label-text  ${methods.formState.errors.fatherHeight
                      ? "validation-message"
                      : ""
                      }`}
                  >
                    Father's Height{" "}
                    <CustomTooltip
                      title="This field is important for automated dose calculation"
                      hideHeader={true}
                    ></CustomTooltip>
                  </FormLabel>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-input"
                  >
                    <CustomTextField
                      placeholder="000.0"
                      fullWidth
                      id="fatherHeight"
                      size="small"
                      inputProps={{
                        ...heightProps,
                      }}
                      {...methods.register("fatherHeight")}
                      onFocus={handleFocus}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">cm</InputAdornment>
                        ),
                      }}
                      error={!!methods.formState.errors.fatherHeight}
                    />
                  </SkeletonWrapper>
                  <Typography className="validation-message">
                    {methods.formState.errors.fatherHeight?.message}
                  </Typography>
                </FormControl>
              </Grid> */}
                </Grid>
                <Grid item xs={12} mt={2}>
                  <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="contained"
                      type="submit"
                      className="primary-button"
                      name="save"
                      disabled={isLoading || methods.formState.isSubmitting}
                    >
                      {opd?.weight > 0 ||
                        opd?.height > 0 ||
                        opd?.bmi > 0 ||
                        opd?.bsa > 0 ||
                        opd?.pulseRate > 0 ||
                        opd?.respiratoryRate > 0 ||
                        (opd?.bpDisplayValue && opd?.bpDisplayValue !== "/") ||
                        opd?.spo2Amount > 0 ||
                        opd?.temperatureAmount > 0 ||
                        opd?.rbs > 0 ||
                        opd?.headCircumference > 0 ||
                        opd?.motherHeight > 0 ||
                        opd?.fatherHeight > 0
                        ? "Update"
                        : "Save"}
                    </LoadingButton>
                    <LoadingButton
                      color="primary"
                      disabled={isLoading || methods.formState.isSubmitting}
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button"
                      name="cancel"
                      onClick={(e) => {
                        handleClosePopUp("CLOSE", null);
                      }}
                    >
                      Cancel
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Box>
      </React.Fragment>
    );
  }
);
export default VitalAndAnthropometry;
